import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, startGoogleSignIn } from "@/lib/auth";
import { Loader2, LogOut, ArrowRight, Copy, Ticket } from "lucide-react";
import { toast } from "sonner";

const MyKits = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      startGoogleSignIn();
      return;
    }
    api.get("/me/kits", { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load kits"))
      .finally(() => setFetching(false));
  }, [user, loading]);

  if (loading || fetching) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#8b0000]" size={32} />
      </div>
    );
  }
  if (!user) return null;

  const code = data?.referral_code || "WELCOME5";
  const orders = data?.orders || [];

  return (
    <div className="grain min-h-screen" data-testid="my-kits-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full border border-[#2d2424]" />}
              <div>
                <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg leading-none">welcome back</div>
                <div className="font-display text-2xl text-[#f4f0ec]">{user.name || user.email}</div>
              </div>
            </div>
          </div>
          <button className="btn-ghost text-sm" onClick={async () => { await logout(); navigate("/"); }}
            data-testid="logout-btn">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="card-noir p-6 relative overflow-hidden mb-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/15 to-transparent pointer-events-none" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Ticket size={16} className="text-[#d4af37]" />
                <div className="text-xs tracking-widest text-[#a39797]">YOUR REFERRAL CODE</div>
              </div>
              <div className="font-display text-4xl text-[#f4f0ec]" data-testid="referral-code">{code}</div>
              <div className="text-sm text-[#a39797] mt-1">$5 off your next case. Enter at checkout.</div>
            </div>
            <button className="btn-primary text-sm" onClick={() => {
              navigator.clipboard.writeText(code);
              toast.success("Code copied");
            }} data-testid="copy-code">
              <Copy size={14} /> Copy code
            </button>
          </div>
        </div>

        <h2 className="font-display text-2xl mb-4">Your cases</h2>
        {orders.length === 0 ? (
          <div className="card-noir p-10 text-center">
            <div className="text-[#a39797] mb-6">No cases yet. Pick one from the vault and get started.</div>
            <Link to="/#cases" className="btn-primary" data-testid="pick-case-btn">
              Pick a case <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <Link key={o.order_id}
                to={`/dashboard/${o.host_token}`}
                className="card-noir p-5 flex items-center justify-between hover:border-[#8b0000] transition-colors"
                data-testid={`order-${o.order_id.slice(0, 8)}`}>
                <div>
                  <div className="font-hand text-[#d4af37] rotate-[-1deg] text-sm leading-none">
                    case #{o.case_number || "001"}
                  </div>
                  <div className="font-display text-xl mt-1">{o.case_title || "The Last Toast"}</div>
                  <div className="text-xs text-[#a39797] mt-1">
                    {o.event_date} · {o.group_size} suspects · {o.event_type}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs tracking-widest px-3 py-1.5 rounded-full border ${
                    o.status === "locked" ? "border-[#d4af37] text-[#d4af37]" :
                    o.status === "refunded" ? "border-[#a39797] text-[#a39797]" :
                    "border-[#8b0000] text-[#8b0000]"
                  }`}>
                    {(o.status || "").replace("_", " ").toUpperCase()}
                  </span>
                  <ArrowRight size={16} className="text-[#a39797]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyKits;
